// Dashboard API service functions - using Configuration API
import { buildConfigApiUrl } from '@api/api';

const getHeaders = () => ({
  'Content-Type': 'application/json',
});

/**
 * Initialize dashboard tables in the database
 */
export const initializeDashboardTables = async () => {
  try {
    const response = await fetch(buildConfigApiUrl('/dashboards/initialize'), {
      method: 'POST',
      headers: getHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return {
      status: 'success',
      data,
    };
  } catch (error) {
    return {
      status: 'error',
      message: `Failed to initialize tables: ${error.message}`,
    };
  }
};

/**
 * Fetch all dashboards from the database
 */
export const fetchDashboards = async () => {
  try {
    const response = await fetch(buildConfigApiUrl('/dashboards'), {
      method: 'GET',
      headers: getHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return {
      status: 'success',
      data,
    };
  } catch (error) {
    return {
      status: 'error',
      message: `Failed to load dashboards: ${error.message}`,
      data: [],
    };
  }
};

/**
 * Fetch a specific dashboard by ID
 */
export const fetchDashboardById = async (id) => {
  try {
    const response = await fetch(buildConfigApiUrl(`/dashboards/${id}`), {
      method: 'GET',
      headers: getHeaders(),
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Dashboard not found');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return {
      status: 'success',
      data,
    };
  } catch (error) {
    return {
      status: 'error',
      message: error.message,
    };
  }
};

/**
 * Create a new dashboard
 */
export const createDashboard = async (dashboardData) => {
  try {
    const response = await fetch(buildConfigApiUrl('/dashboards'), {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({
        name: dashboardData.name,
        description: dashboardData.description || '',
        type: dashboardData.type,
        status: dashboardData.status,
        creator: dashboardData.creator,
        requiredFilters: dashboardData.requiredFilters,
        widgets: dashboardData.widgets || [],
      }),
    });

    if (!response.ok) {
      // Handle specific HTTP status codes
      if (response.status === 409) {
        // Conflict - duplicate name
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'A dashboard with this name already exists');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return {
      status: 'success',
      data,
    };
  } catch (error) {
    return {
      status: 'error',
      message: error.message,
    };
  }
};

/**
 * Update an existing dashboard
 */
export const updateDashboard = async (id, dashboardData) => {
  try {
    // Build the request body, only including fields that are provided
    const body = {};

    if (dashboardData.name !== undefined) body.name = dashboardData.name;
    if (dashboardData.description !== undefined) body.description = dashboardData.description;
    if (dashboardData.type !== undefined) body.type = dashboardData.type;
    if (dashboardData.status !== undefined) body.status = dashboardData.status;
    if (dashboardData.creator !== undefined) body.creator = dashboardData.creator;
    if (dashboardData.requiredFilters !== undefined) body.requiredFilters = dashboardData.requiredFilters;
    // Only include widgets if explicitly provided (not undefined)
    if (dashboardData.widgets !== undefined) body.widgets = dashboardData.widgets;

    const response = await fetch(buildConfigApiUrl(`/dashboards/${id}`), {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      // Handle specific HTTP status codes
      if (response.status === 404) {
        throw new Error('Dashboard not found');
      }
      if (response.status === 409) {
        // Conflict - duplicate name
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'A dashboard with this name already exists');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return {
      status: 'success',
      data,
    };
  } catch (error) {
    return {
      status: 'error',
      message: error.message,
    };
  }
};

/**
 * Delete a dashboard
 */
export const deleteDashboard = async (id) => {
  try {
    const response = await fetch(buildConfigApiUrl(`/dashboards/${id}`), {
      method: 'DELETE',
      headers: getHeaders(),
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Dashboard not found');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return {
      status: 'success',
    };
  } catch (error) {
    return {
      status: 'error',
      message: error.message,
    };
  }
};

/**
 * Fetch all dashboard templates
 */
export const fetchDashboardTemplates = async () => {
  try {
    const response = await fetch(buildConfigApiUrl('/dashboards/templates/list'), {
      method: 'GET',
      headers: getHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return {
      status: 'success',
      data,
    };
  } catch (error) {
    return {
      status: 'error',
      message: `Failed to load templates: ${error.message}`,
      data: [],
    };
  }
};

/**
 * Save a dashboard as a template
 */
export const saveDashboardAsTemplate = async (id, templateName) => {
  try {
    const response = await fetch(buildConfigApiUrl(`/dashboards/${id}/save-as-template`), {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ templateName }),
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Dashboard not found');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return {
      status: 'success',
      data,
    };
  } catch (error) {
    return {
      status: 'error',
      message: error.message,
    };
  }
};

/**
 * Load a dashboard from a template
 */
export const loadDashboardFromTemplate = async (templateId, dashboardName) => {
  try {
    const response = await fetch(buildConfigApiUrl(`/dashboards/load-from-template/${templateId}`), {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ dashboardName }),
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Template not found');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return {
      status: 'success',
      data,
    };
  } catch (error) {
    return {
      status: 'error',
      message: error.message,
    };
  }
};

/**
 * Search reports with pagination, filtering, and sorting
 */
export const searchReports = async ({
  page = 1,
  pageSize = 50,
  sortField = 'updatedAt',
  sortOrder = 'desc',
  filters = [],
  searchTerm = '',
}) => {
  try {
    const response = await fetch(buildConfigApiUrl('/dashboards/search'), {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({
        page,
        pageSize,
        sortField,
        sortOrder,
        filters,
        searchTerm,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return {
      rows: data.data || [],
      total: data.total || 0,
    };
  } catch {
    return {
      rows: [],
      total: 0,
    };
  }
};

/**
 * Bulk update dashboard status (publish/unpublish)
 */
export const bulkUpdateStatus = async ({ ids, status }) => {
  try {
    const response = await fetch(buildConfigApiUrl('/dashboards/bulk-status'), {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify({ ids, status }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return {
      status: 'success',
      data,
    };
  } catch (error) {
    return {
      status: 'error',
      message: error.message,
    };
  }
};

/**
 * Bulk delete dashboards
 */
export const bulkDelete = async ({ ids }) => {
  try {
    const response = await fetch(buildConfigApiUrl('/dashboards/bulk/delete'), {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ ids }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return {
      status: 'success',
      data,
    };
  } catch (error) {
    return {
      status: 'error',
      message: error.message,
    };
  }
};

/**
 * Convert UI filters to API format
 */
export const convertReportFiltersToApiFormat = (filters = []) => filters
  .filter((f) => f.type && f.value)
  .map((filter) => {
    const value = Array.isArray(filter.value) ? filter.value[0] : filter.value;
    return {
      type: filter.type,
      value: value?.value !== undefined ? value.value : value,
    };
  })
  .filter((f) => f.value !== undefined && f.value !== null && f.value !== '');

/**
 * Map API filter options to react-select dropdown format
 */
const mapFilterOptionsToDropdownFormat = (data) => ({
  name: (data.name || []).map((name) => ({
    value: name,
    label: name,
  })),
  description: (data.description || []).map((description) => ({
    value: description,
    label: description,
  })),
  creator: (data.creator || []).map((creator) => ({
    value: creator,
    label: creator,
  })),
  updatedAt: (data.updatedAt || []).map((date) => ({
    value: date,
    label: date,
  })),
});

/**
 * Get filter options for reports/dashboards
 * Returns available values for name and creator filters
 */
export const getReportFilterOptions = async (currentFilters = [], searchTerm = '') => {
  try {
    const requestBody = {
      filters: convertReportFiltersToApiFormat(currentFilters),
    };
    if (searchTerm) {
      requestBody.searchTerm = searchTerm;
    }

    const response = await fetch(buildConfigApiUrl('/dashboards/search/filters'), {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch filter options');
    }

    const data = await response.json();
    return mapFilterOptionsToDropdownFormat(data);
  } catch (error) {
    console.error('Error fetching report filter options:', error);
    throw error;
  }
};

/**
 * Get dashboard dependencies (saved reports and scheduled reports that use this dashboard)
 * Used to show warnings when editing or publishing a report
 * @param {number} dashboardId - The dashboard ID to check dependencies for
 * @returns {Promise<Object>} - Object containing savedReports and scheduledReports arrays with counts
 */
export const getDashboardDependencies = async (dashboardId) => {
  try {
    const response = await fetch(buildConfigApiUrl(`/dashboards/${dashboardId}/dependencies`), {
      method: 'GET',
      headers: getHeaders(),
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Dashboard not found');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return {
      status: 'success',
      data,
    };
  } catch (error) {
    return {
      status: 'error',
      message: error.message,
      data: {
        dashboardId,
        savedReportsCount: 0,
        scheduledReportsCount: 0,
        savedReports: [],
        scheduledReports: [],
      },
    };
  }
};
